// src/components/RegionDetailsTab.js
import React, { useEffect, useCallback, useState, useMemo, memo } from 'react';
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Button,
    Card,
    CardContent,
    IconButton,
    Tabs,
    Tab,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import RegionForm from './RegionForm';
import api from '../api';
import { convertPdfToImages } from '../utils/pdfUtils';

const REGION_NAMES_MANDIBLE = {
    "RIGHT MOLAR": ['48', '47', '46'],
    "RIGHT PRE-MOLAR": ['45', '44'],
    "ANTERIOR (c-c)": ['43', '42', '41', '31', '32', '33'],
    "LEFT PRE-MOLAR": ['34', '35'],
    "LEFT MOLAR": ['36', '37', '38']
};

const REGION_NAMES_MAXILLA = {
    "RIGHT PTERYGOID": ['right_pterygoid'],
    "RIGHT MOLAR": ['18', '17', '16'],
    "RIGHT PRE-MOLAR": ['15', '14'],
    "ANTERIOR (c-c)": ['13', '12', '11', '21', '22', '23'],
    "LEFT PRE-MOLAR": ['24', '25'],
    "LEFT MOLAR": ['26', '27', '28'],
    "LEFT PTERYGOID": ['left_pterygoid']
};

// Memoized Tab component to prevent unnecessary re-renders
const MemoizedTab = memo(({ label, value, ...props }) => (
    <Tab
        label={label}
        value={value}
        {...props}
        sx={{
            minWidth: '60px',
            padding: '6px 16px',
            margin: '6px 3px',
            borderRadius: '8px',
            backgroundColor: '#e0e0e0',
            color: '#000',
            '&.Mui-selected': {
                backgroundColor: '#1976d2',
                color: '#fff',
            },
            '&:hover': {
                backgroundColor: '#d0d0d0',
            }
        }}
    />
));

// Memoized RegionForm wrapper to prevent unnecessary re-renders
const MemoizedRegionFormWrapper = memo(({
    nodeId,
    isActive,
    reportType,
    onRegionDataChange,
    isFirstSubregion
}) => {
    const parts = nodeId.split('-');
    const regionNumber = parts[parts.length - 1];
    const regionName = parts.slice(0, -1).join('-');

    // Only render the form if the tab is active
    if (!isActive) {
        return null;
    }

    return (
        <RegionForm
            reportType={reportType}
            regionName={regionName}
            regionNumber={regionNumber}
            onRegionDataChange={onRegionDataChange}
            isFirstSubregion={isFirstSubregion}
        />
    );
});

const RegionDetailsTab = () => {
    const { state, actions } = useFormContext();
    const localState = state.regionDetailsPdfState;

    const [activeTab, setActiveTab] = useState(null);

    // Memoize regions to display to prevent recalculation
    const regionsToDisplay = useMemo(() => {
        return state.reportType.includes("Mandible")
            ? REGION_NAMES_MANDIBLE
            : REGION_NAMES_MAXILLA;
    }, [state.reportType]);

    // Memoize sorted selected regions
    const sortedSelected = useMemo(() => {
        const leafNodes = (localState.selected || []).filter(nodeId =>
            !Object.keys(regionsToDisplay).includes(nodeId)
        );

        const regionOrder = [];
        Object.entries(regionsToDisplay).forEach(([regionName, teeth]) => {
            teeth.forEach(tooth => {
                regionOrder.push(`${regionName}-${tooth}`);
            });
        });

        return leafNodes.sort((a, b) => {
            const indexA = regionOrder.indexOf(a);
            const indexB = regionOrder.indexOf(b);
            return indexA - indexB;
        });
    }, [localState.selected, regionsToDisplay]);

    // Memoize all item IDs
    const allItemIds = useMemo(() => {
        return Object.keys(regionsToDisplay).flatMap(regionName =>
            [regionName, ...regionsToDisplay[regionName].map(tooth => `${regionName}-${tooth}`)]
        );
    }, [regionsToDisplay]);

    // Memoize selection state
    const selectionState = useMemo(() => {
        const selectedCount = (localState.selected || []).length;
        const allItemsCount = allItemIds.length;
        const isAllSelected = selectedCount === allItemsCount;
        const isIndeterminate = selectedCount > 0 && selectedCount < allItemsCount;

        return { selectedCount, allItemsCount, isAllSelected, isIndeterminate };
    }, [localState.selected, allItemIds]);

    const sortedSelectedString = JSON.stringify(sortedSelected);
    const hasAutoPopulated = React.useRef(false);

    // Auto-populate Region Details Pdf State selected objects when loaded from localStorage 
    // to keep the frontend view synchronized with active data mapping pulling from the raw input states
    useEffect(() => {
        if (!hasAutoPopulated.current && Object.keys(state.regionFormStates || {}).length > 0) {
            const currentSelected = localState.selected || [];
            if (currentSelected.length === 0) {
                // Find IDs matching standard leaf nodes that hold our active region numbers
                const newSelection = [];
                Object.entries(state.regionFormStates).forEach(([regionNumber, regionState]) => {
                    const formData = regionState.formData || {};
                    if (Object.keys(formData).length > 0) {
                        const match = allItemIds.find(id => id.endsWith(`-${regionNumber}`) || id === regionNumber);
                        if (match) newSelection.push(match);
                    }
                });

                if (newSelection.length > 0) {
                    actions.updateRegionDetailsPdfState({
                        selected: newSelection
                    });
                }
            }
            hasAutoPopulated.current = true;
        }
    }, [state.regionFormStates, localState.selected, allItemIds, actions]);

    // Auto-select first tab when selection changes
    useEffect(() => {
        const sortedSelected = JSON.parse(sortedSelectedString);
        if (sortedSelected.length > 0) {
            // If the active tab is no longer in the list of selected tabs,
            // or if there is no active tab, set it to the first selected tab.
            setActiveTab(currentActiveTab => {
                if (!currentActiveTab || !sortedSelected.includes(currentActiveTab)) {
                    return sortedSelected[0];
                }
                return currentActiveTab;
            });
        } else {
            // If there are no selected tabs, there should be no active tab.
            setActiveTab(null);
        }
    }, [sortedSelectedString]);

    // Memoized handlers to prevent recreation on every render
    const handleTabChange = useCallback((event, newValue) => {
        setActiveTab(newValue);
    }, []);

    const handleSelectAll = useCallback(() => {
        if (selectionState.isAllSelected) {
            actions.updateRegionDetailsPdfState({ selected: [], expanded: [] });
            setTimeout(() => {
                Object.keys(state.regionFormStates || {}).forEach(regionNumber => {
                    actions.removeRegionData(regionNumber);
                });
            }, 50);
        } else {
            actions.updateRegionDetailsPdfState({
                selected: allItemIds,
                expanded: Object.keys(regionsToDisplay)
            });
        }
    }, [selectionState.isAllSelected, allItemIds, regionsToDisplay, actions, state.regionFormStates]);

    const handleToggle = useCallback((event, nodeIds) => {
        actions.updateRegionDetailsPdfState({ expanded: nodeIds });
    }, [actions]);

    const handleSelectToggle = useCallback((event, itemId, isSelected) => {
        const regions = regionsToDisplay;
        const currentSelected = localState.selected || [];
        const newSelected = new Set(currentSelected);

        const children = (regions[itemId] || []).map(tooth => `${itemId}-${tooth}`);
        const parent = Object.keys(regions).find(p => regions[p].some(t => `${p}-${t}` === itemId));

        if (isSelected) {
            newSelected.add(itemId);
            children.forEach(child => newSelected.add(child));
            if (parent) {
                const siblings = regions[parent].map(t => `${parent}-${t}`);
                const allSiblingsSelected = siblings.every(sibling => newSelected.has(sibling) || sibling === itemId);
                if (allSiblingsSelected) {
                    newSelected.add(parent);
                }
            }
        } else {
            newSelected.delete(itemId);
            
            const regionsToRemove = [];
            if (regions[itemId]) {
                regions[itemId].forEach(tooth => regionsToRemove.push(tooth));
            } else {
                const tooth = itemId.includes('-') ? itemId.split('-').pop() : itemId;
                regionsToRemove.push(tooth);
            }
            
            setTimeout(() => {
                regionsToRemove.forEach(tooth => {
                    actions.removeRegionData(tooth);
                });
            }, 50);

            children.forEach(child => newSelected.delete(child));
            if (parent) {
                newSelected.delete(parent);
            }
        }

        const finalSelection = Array.from(newSelected);

        // Batch parent selection updates
        Object.keys(regions).forEach(parent => {
            const children = regions[parent].map(t => `${parent}-${t}`);
            const allChildrenSelected = children.every(child => finalSelection.includes(child));
            if (allChildrenSelected && !finalSelection.includes(parent)) {
                finalSelection.push(parent);
            } else if (!allChildrenSelected && finalSelection.includes(parent)) {
                const index = finalSelection.indexOf(parent);
                if (index > -1) {
                    finalSelection.splice(index, 1);
                }
            }
        });

        const parentNodes = new Set();
        finalSelection.forEach(regionId => {
            const parent = Object.keys(regionsToDisplay).find(p => regionsToDisplay[p].some(t => `${p}-${t}` === regionId));
            if (parent) {
                parentNodes.add(parent);
            }
        });

        actions.updateRegionDetailsPdfState({
            selected: finalSelection,
            expanded: [...parentNodes]
        });
    }, [regionsToDisplay, localState.selected, actions]);

    const handlePdfFileChange = useCallback(async (event) => {
        const file = event.target.files[0];
        if (file) {
            actions.updateRegionDetailsPdfState({
                pdfFile: file,
                loadingPdf: true,
                error: null,
                convertedImages: []
            });

            try {
                const images = await convertPdfToImages(file);
                actions.updateRegionDetailsPdfState({
                    convertedImages: images,
                    loadingPdf: false
                });
            } catch (err) {
                console.error("PDF Conversion Error:", err);
                actions.updateRegionDetailsPdfState({
                    error: 'Failed to extract images from PDF.',
                    loadingPdf: false
                });
            }
        }
    }, [actions]);

    const handleRemovePdf = useCallback(() => {
        actions.updateRegionDetailsPdfState({
            pdfFile: null,
            convertedImages: [],
            error: null
        });
    }, [actions]);

    const handleRegionDataChange = useCallback((regionNumber, updates) => {
        actions.updateRegionData(regionNumber, updates);
    }, [actions]);

    const handleClearAllRegions = useCallback(() => {
        actions.updateRegionDetailsPdfState({ selected: [], expanded: [] });
        setTimeout(() => {
            Object.keys(state.regionFormStates || {}).forEach(regionNumber => {
                actions.removeRegionData(regionNumber);
            });
        }, 50);
    }, [actions, state.regionFormStates]);

    // Early return for no patient data
    if (!state.patientData) {
        return (
            <Box p={3}>
                <Typography variant="h6" color="textSecondary">
                    Please load patient data first from the sidebar.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box p={3}>
                <Typography variant="h6" gutterBottom>
                    Region Details
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Report Type: {state.reportType}
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="subtitle1">
                                        Select PDF file for Measurements
                                    </Typography>
                                    {localState.pdfFile && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={handleRemovePdf}
                                            title="Remove PDF"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Box>

                                <Button variant="contained" component="label" sx={{ mb: 2 }}>
                                    {localState.pdfFile ? 'Replace PDF File' : 'Select PDF File'}
                                    <input
                                        hidden
                                        type="file"
                                        onChange={handlePdfFileChange}
                                        accept=".pdf"
                                    />
                                </Button>

                                {localState.pdfFile && (
                                    <Typography variant="caption" display="block" color="success.main">
                                        Selected: {localState.pdfFile.name}
                                    </Typography>
                                )}

                                {localState.loadingPdf && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        <Typography variant="body2">Converting PDF to images...</Typography>
                                    </Box>
                                )}

                                {localState.error && (
                                    <Alert severity="error" sx={{ mt: 2 }}>{localState.error}</Alert>
                                )}

                                {localState.convertedImages.length > 0 && (
                                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                        ✓ PDF converted successfully ({localState.convertedImages.length} pages)
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box display="flex" alignItems="center">
                                        <Checkbox
                                            checked={selectionState.isAllSelected}
                                            indeterminate={selectionState.isIndeterminate}
                                            onChange={handleSelectAll}
                                            inputProps={{ 'aria-label': 'select all regions' }}
                                        />
                                        <Typography variant="subtitle1">
                                            Select Regions
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {(localState.selected || []).length > 0 && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={handleClearAllRegions}
                                                startIcon={<DeleteIcon />}
                                            >
                                                Clear All
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                <SimpleTreeView
                                    expandedItems={localState.expanded || []}
                                    selectedItems={localState.selected || []}
                                    onExpandedItemsChange={handleToggle}
                                    onItemSelectionToggle={handleSelectToggle}
                                    multiSelect
                                    checkboxSelection
                                    sx={{
                                        height: 240,
                                        flexGrow: 1,
                                        overflowY: 'auto',
                                        border: '1px solid #ccc',
                                        p: 1,
                                        borderRadius: 1
                                    }}
                                >
                                    {Object.keys(regionsToDisplay).map(regionName => (
                                        <TreeItem key={regionName} itemId={regionName} label={regionName}>
                                            {regionsToDisplay[regionName].map(tooth => (
                                                <TreeItem
                                                    key={`${regionName}-${tooth}`}
                                                    itemId={`${regionName}-${tooth}`}
                                                    label={tooth}
                                                />
                                            ))}
                                        </TreeItem>
                                    ))}
                                </SimpleTreeView>

                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    Selected regions: {sortedSelected.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {sortedSelected.length > 0 && activeTab && (
                <Box sx={{
                    position: 'sticky',
                    top: '64px',
                    zIndex: 1000,
                    backgroundColor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    boxShadow: 1
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Selected region tabs"
                        sx={{
                            backgroundColor: 'aliceblue',
                        }}
                    >
                        {sortedSelected.map(nodeId => {
                            const regionNumber = nodeId.split('-').pop();
                            return (
                                <MemoizedTab
                                    key={nodeId}
                                    label={regionNumber}
                                    value={nodeId}
                                />
                            );
                        })}
                    </Tabs>
                </Box>
            )}

            <Box sx={{ p: 3 }}>
                {sortedSelected.map(nodeId => {
                    const regionName = nodeId.substring(0, nodeId.lastIndexOf('-'));

                    // Find all selected subregions for the current header region
                    const selectedSubregionsForHeader = sortedSelected.filter(id => id.startsWith(regionName));

                    // The first subregion is the first element in this filtered array
                    const isFirst = selectedSubregionsForHeader[0] === nodeId;

                    return (
                        <MemoizedRegionFormWrapper
                            key={nodeId}
                            nodeId={nodeId}
                            isActive={activeTab === nodeId}
                            reportType={state.reportType}
                            onRegionDataChange={handleRegionDataChange}
                            isFirstSubregion={isFirst}
                        />
                    );
                })}

                {activeTab === null && sortedSelected.length === 0 && (
                    <Box sx={{
                        textAlign: 'center',
                        py: 4,
                        color: 'text.secondary',
                        border: '2px dashed #ccc',
                        borderRadius: 1
                    }}>
                        <Typography variant="h6" gutterBottom>
                            No regions selected
                        </Typography>
                        <Typography variant="body2">
                            Please select regions from the tree above to add details
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Summary:
                </Typography>
                <Typography variant="body2">
                    PDF File: {localState.pdfFile ? 'Uploaded' : 'Not uploaded'}
                </Typography>
                <Typography variant="body2">
                    Regions selected: {sortedSelected.length}
                </Typography>
                <Typography variant="body2">
                    Regions with data: {Object.keys(state.regionDetailsData || {}).length}
                </Typography>
            </Box>
        </Box>
    );
};

export default RegionDetailsTab;
